import React, { useState, useRef, useEffect, useCallback } from 'react'; // Import useCallback
import axios from 'axios';

const ContentSlides = () => {
  const galleryRef = useRef(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(1);
  const [touchStartX, setTouchStartX] = useState(0); // State for touch start X coordinate
  const [showNavIcons, setShowNavIcons] = useState(true); // State to control nav icon visibility

  const API_URL = 'https://ethiccraft.org/backend/camp_highlights/read_isShown.php'; // Replace with your actual endpoint
  const IMAGE_PREFIX = 'https://ethiccraft.org/';

  // Function to check screen width and update showNavIcons state
  const checkScreenWidth = useCallback(() => {
    setShowNavIcons(window.innerWidth >= 868);
  }, []);

  useEffect(() => {
    // Initial check on mount
    checkScreenWidth();
    // Add event listener for window resize
    window.addEventListener('resize', checkScreenWidth);
    // Cleanup event listener on component unmount
    return () => window.removeEventListener('resize', checkScreenWidth);
  }, [checkScreenWidth]); // Dependency array includes checkScreenWidth

  useEffect(() => {
    // Fetch images from the API
    const fetchImages = async () => {
      try {
        const response = await axios.get(API_URL);
        const images = response.data.map(img => IMAGE_PREFIX + img.image); // assuming { image: "path/to/image.jpg" }
        setGalleryImages(images);
      } catch (error) {
        console.error('Error fetching images:', error);
      }
    };

    fetchImages();
  }, []);

  const scrollToItem = (index) => {
    if (galleryRef.current && galleryRef.current.children[index]) {
      galleryRef.current.children[index].scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      });
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      scrollToItem(currentIndex);
    }, 100);
    return () => clearTimeout(timer);
  }, [currentIndex, galleryImages]);

  const handlePrev = useCallback(() => {
    setCurrentIndex((prevIndex) => Math.max(0, prevIndex - 1));
  }, []); // No dependencies as it only uses state setter

  const handleNext = useCallback(() => {
    setCurrentIndex((prevIndex) => Math.min(galleryImages.length - 1, prevIndex + 1));
  }, [galleryImages.length]); // Depends on galleryImages.length

  // Touch event handlers
  const handleTouchStart = useCallback((e) => {
    setTouchStartX(e.touches[0].clientX);
  }, []);

  const handleTouchMove = useCallback((e) => {
    // It's generally good practice to prevent default on touchmove for carousels
    // to avoid native scrolling interference, especially if the content inside
    // the slider is not scrollable itself.
    e.preventDefault(); 
  }, []);

  const handleTouchEnd = useCallback((e) => {
    const touchEndX = e.changedTouches[0].clientX;
    const deltaX = touchStartX - touchEndX;
    const swipeThreshold = 50; // Minimum horizontal swipe distance to trigger a slide change

    if (deltaX > swipeThreshold) {
      handleNext(); // Swipe left, go to next slide
    } else if (deltaX < -swipeThreshold) {
      handlePrev(); // Swipe right, go to previous slide
    }
  }, [touchStartX, handleNext, handlePrev]);


  return (
    <div className="triglav-section">
      {/* Header Section - Main title outside the box */}
      <div className="triglav-header">
        <h1 className="triglav-title">
          🌍 <strong>Ethiccraft Camps & Tours:<br></br>Learning Beyond the Classroom</strong>
        </h1>

        {/* Styled box containing subtitle and intro */}
        <div className="triglav-header-content-box fade-in delay-100">
          {/* Subtitle */}
          <p className="triglav-subtitle">
            At Ethiccraft Club, we believe <strong>real learning often happens outside traditional settings.</strong> Our camps and tours are designed to give students exposure to India's diverse culture, history, and values through direct experiences — helping them grow as individuals and as leaders.
          </p>

          <div className="header-box-separator"></div> {/* Separator inside the box */}

          {/* Introduction Section - Now inside the box */}
          <div className="triglav-intro-in-box">
            <div className="intro-column">
              <p>
                🚌 <strong>Where We’ve Been</strong><br />
                Our teams have organized student tours and learning experiences in cities across India, including:
              </p>
              <p>
                <strong>Ayodhya, Jaipur, Kurukshetra, Rishikesh, Mussoorie, Haridwar, Agra, Mathura, Delhi, and Mayapur.</strong>
              </p>
              <p>
                Each destination brings unique lessons in teamwork, culture, and social responsibility. These camps are <strong>not vacations — they’re platforms for bonding, exploration, and understanding India’s rich diversity.</strong>
              </p>

              {/* New paragraph inserted here */}
              <div style={{ marginTop: "1.5rem" }}>
                <h2 style={{ fontWeight: "bold", marginBottom: "0.5rem" }}>🌟 Why Ethiccraft Camps Matter</h2>
                <p>Ethiccraft camps are immersive experiences designed to:
                  <strong>Build ethical leadership rooted in our mission</strong>
                  <strong>Develop strong character and integrity</strong>
                  <strong>Foster teamwork and empathy</strong>
                  <strong>Increase cultural awareness and social responsibility</strong></p>

                {/* <p>
                  These programs equip students with practical skills and values to become responsible leaders committed to positive change.
                </p> */}
              </div>
            </div>

            <div className="intro-column">
              <p>
                💬 <strong>Student-Led, Team-Supported</strong><br />
                These camps are often coordinated by campus representatives or club leaders with support from the central Ethiccraft team. All collaborations or event ideas are approved in coordination with our core team to ensure alignment with Ethiccraft's values and vision.
              </p>
              <p>
                🌱 <strong>Impact and Experience</strong><br />
                Over time, these journeys have helped build strong campus communities, empowered volunteers, and nurtured responsible leaders across colleges. They offer practical lessons in organization, communication, and team building — all in a real-world setting.
              </p>
              <p>
                🚀 <strong>Want to Be Part of the Next Camp?</strong><br />
                Stay connected through our website or campus chapter to know when the next Ethiccraft tour is scheduled. Whether you're looking to participate or help organize, there’s a place for you.
              </p>
            </div>
          </div>
        </div>

        {/* Separator below the large box, above the icon */}
        <div className="header-separator fade-in delay-300"></div>

        <p className="triglav-footer-quote" style={{ marginTop: "1rem", fontStyle: "italic" }}>
          Because some lessons can't be learned in a lecture hall — <strong>they must be lived.</strong>
        </p>
      </div>

      <div className="section-icon fade-in delay-400">
        <div className="mountain-icon">▲</div>
      </div>

      <div className="triglav-gallery fade-in delay-500">
        <div
          className="gallery-container"
          ref={galleryRef}
          onTouchStart={handleTouchStart} // Add touch event listeners
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {galleryImages.map((image, index) => (
            <div
              key={index}
              className={`gallery-item ${index === currentIndex ? 'central-gallery-item' : ''}`}
              onClick={() => setCurrentIndex(index)}
            >
              <img
                src={image}
                alt={`gallery-${index}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
          ))}
        </div>

        {/* Conditionally render navigation based on showNavIcons state */}
        {showNavIcons && currentIndex > 0 && (
          <div className="gallery-nav left" onClick={handlePrev}>&lt;</div>
        )}
        {showNavIcons && currentIndex < galleryImages.length - 1 && (
          <div className="gallery-nav right" onClick={handleNext}>&gt;</div>
        )}
        <div className="gallery-separator"></div>
      </div>
    </div>
  );
};

export default ContentSlides;